import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, CalendarEvent } from '../../../../components/Calendar';
import { useAppPostCallPlans } from '../../../../hooks/useAppPostCallPlan';
import { Heading } from '../../../../components/Heading';
import { Button } from '../../../../components/Button';
import { Badge } from '../../../../components/Badge';
import Loading from '../../../../components/Loading';
import { UserContext } from '../../../../context/userContext';
import dateFormat from 'dateformat';

const PostCallPlanCalendarPage: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const { loading, error, fetchPostCallPlansPaginated, postCallPlans } = useAppPostCallPlans();
    const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

    useEffect(() => {
        document.title = "Post Call Plans - Calendar View";
        // Fetch all post-call plans for the calendar
        fetchPostCallPlansPaginated(1, 1000); // Fetch all plans for now
    }, [fetchPostCallPlansPaginated]);

    useEffect(() => {
        if (postCallPlans.length > 0) {
            // Convert post-call plans to calendar events
            const events: CalendarEvent[] = postCallPlans.map(plan => ({
                id: plan.id,
                title: `Post-Call: ${plan.customer_fullname || 'Unknown Customer'}`,
                date: plan.created_time, // Use created_time for post-call plans
                type: 'postcall' as const,
                customer: plan.customer_fullname || 'Unknown Customer',
                salesman: plan.salesman_username || 'Unknown Salesman',
                status: 'Completed' // Post-call plans are always completed
            }));
            setCalendarEvents(events);
        }
    }, [postCallPlans]);

    const handleEventClick = (event: CalendarEvent) => {
        setSelectedEvent(event);
        // Navigate to the post-call plan view
        navigate(`/gp/app/postcallplans/view/${event.id}`);
    };

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
        // You could implement date-specific actions here
        console.log('Selected date:', date);
    };

    const getPlansForSelectedDate = () => {
        if (!selectedDate) return [];
        const dateString = selectedDate.toDateString();
        return calendarEvents.filter(event =>
            new Date(event.date).toDateString() === dateString
        );
    };

    const getRecentPlans = () => {
        const today = new Date();
        const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        return calendarEvents
            .filter(event => new Date(event.date) >= oneWeekAgo)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .slice(0, 5);
    };

    const getPlansByMonth = () => {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        return calendarEvents.filter(event => {
            const eventDate = new Date(event.date);
            return eventDate.getMonth() === currentMonth && eventDate.getFullYear() === currentYear;
        });
    };

    if (loading) return <Loading />;
    if (error) return <p className="text-red-600 text-center py-8">{error}</p>;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-4">
                <Heading>Post Call Plans - Calendar View</Heading>
                <div className="flex items-center space-x-3">
                    <Button onClick={() => navigate('/gp/app/postcallplans')} outline>
                        List View
                    </Button>
                </div>
            </div>

            {/* Calendar */}
            <Calendar
                events={calendarEvents}
                onEventClick={handleEventClick}
                onDateClick={handleDateClick}
            />

            {/* Additional Information Tabs */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Selected Date Details */}
                {selectedDate && (
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Plans for {dateFormat(selectedDate, "mmmm dd, yyyy")}
                        </h3>
                        {getPlansForSelectedDate().length > 0 ? (
                            <div className="space-y-3">
                                {getPlansForSelectedDate().map(event => (
                                    <div
                                        key={event.id}
                                        className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleEventClick(event)}
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="font-medium text-gray-900">{event.customer}</div>
                                                <div className="text-sm text-gray-500">{event.salesman}</div>
                                            </div>
                                            <Badge color="green">
                                                {event.status}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 text-center py-4">No plans completed on this date</p>
                        )}
                    </div>
                )}

                {/* Quick Stats */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Overview</h3>
                    <div className="space-y-4">
                        <div>
                            <div className="text-sm font-medium text-gray-500">This Month</div>
                            <div className="text-2xl font-bold text-blue-600">{getPlansByMonth().length}</div>
                        </div>
                        <div>
                            <div className="text-sm font-medium text-gray-500">Recent (Last 7 Days)</div>
                            <div className="text-2xl font-bold text-green-600">{getRecentPlans().length}</div>
                        </div>
                        <div>
                            <div className="text-sm font-medium text-gray-500">Total Plans</div>
                            <div className="text-2xl font-bold text-gray-900">{calendarEvents.length}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent Plans List */}
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Plans</h3>
                </div>
                <div className="p-6">
                    {getRecentPlans().length > 0 ? (
                        <div className="space-y-3">
                            {getRecentPlans().map(event => (
                                <div
                                    key={event.id}
                                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                                    onClick={() => handleEventClick(event)}
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        <div>
                                            <div className="font-medium text-gray-900">{event.customer}</div>
                                            <div className="text-sm text-gray-500">
                                                {event.salesman} • {dateFormat(event.date, "mmm dd, yyyy")}
                                            </div>
                                        </div>
                                    </div>
                                    <Badge color="green">
                                        {event.status}
                                    </Badge>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-4">No recent plans</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PostCallPlanCalendarPage;
